import React from 'react';

import { useIntl } from 'react-intl';

import { useDispatch, useSelector } from 'react-redux';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { selectUserCurrency } from '@/store/selectors/userSelectors';
import { formatCurrency } from '@/utils/currency-utils';
import { ButtonWithLoader } from '@/components/Common/ButtonWithLoader';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import { getUserWallet } from '@/store/wallet/actions';
import { BffApiError } from '@/models/bff-api-error';
import { generateBffErrorMessageWithCode } from '@/utils/utils';
import { getBonuses } from '@/store/bonuses/actions';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';

interface ForfeitButtonProps {
  bonusMoney: number;
  realBonusMoney: number;
  userBonusId: number;
  bonusName: string;
}

const ForfeitButton = ({
  bonusMoney,
  realBonusMoney,
  bonusName,
  userBonusId,
}: ForfeitButtonProps) => {
  const locale = useSelector(selectLocale);
  const currency = useSelector(selectUserCurrency);
  const dispatch = useDispatch();
  const intl = useIntl();
  const { request, loading } = useBffApiRequest();
  const { openModal, isOpen, closeModal } = useModal();

  const forfeitHandler = (userBonusId: number) => {
    if (isOpen) {
      openModal(MODALS.CONFIRM, {
        loading: true,
      });
    }
    request({
      apiMethod: BffApiService.forfeitBonus,
      pathParams: {
        userBonusId,
      },
      successCallback: () => {
        dispatch(getBonuses());
        dispatch(getUserWallet());
        closeModal();
      },
      errorCallback: (error: BffApiError) => {
        if (isOpen) {
          openModal(MODALS.CONFIRM, {
            loading: false,
            error: generateBffErrorMessageWithCode(
              intl.formatMessage({ id: 'error.support' }),
              error.code,
            ),
          });
        }
      },
    });
  };

  const openModalHandler = () => {
    const bonusMoneyFormatted = formatCurrency(bonusMoney, locale, currency);
    const realBonusMoneyFormatted = formatCurrency(realBonusMoney, locale, currency);

    if (realBonusMoney > 0) {
      openModal(MODALS.CONFIRM, {
        header: intl.formatMessage({ id: 'activeBonuses.confirm.header' }),
        text: intl.formatMessage(
          { id: 'activeBonuses.confirm.text1' },
          {
            bonusName,
            bonusMoney: bonusMoneyFormatted,
            realBonusMoney: realBonusMoneyFormatted,
          },
        ),
        buttonText: intl.formatMessage({ id: 'activeBonuses.forfeit' }),
        onSubmit: () => forfeitHandler(userBonusId),
        loading: false,
      });
      return;
    }

    openModal(MODALS.CONFIRM, {
      header: intl.formatMessage({ id: 'activeBonuses.confirm.header' }),
      text: intl.formatMessage(
        { id: 'activeBonuses.confirm.text2' },
        { bonusName, bonusMoney: bonusMoneyFormatted },
      ),
      buttonText: intl.formatMessage({ id: 'activeBonuses.forfeit' }),
      onSubmit: () => forfeitHandler(userBonusId),
      loading: false,
    });
  };

  return (
    <ButtonWithLoader
      message={intl.formatMessage({ id: 'activeBonuses.forfeit' })}
      onClick={openModalHandler}
      loading={loading}
      disabled={loading}
      className="bg-emerald-500 hover:bg-emerald-600 !my-0 !text-base h-auto py-4 px-6"
    />
  );
};

export default ForfeitButton;
